import * as React from "react";
import { Stack, Columns, Column, Heading } from "@origin-digital/ods-core";
import { Text, Button, Colors } from "@origin-digital/style-guide";
import content from "../../cms/chat.json";

export enum StatusType {
  AVAILABLE = "AVAILABLE",
  UNAVAILABLE = "UNAVAILABLE",
  BUSY = "BUSY",
  WAITING = "WAITING",
  LOADING = "LOADING",
  ERROR = "ERROR",
}

export interface ILiveChatItem {
  title: string;
  icon: React.ReactNode;
  line1: string;
  line2: string;
  status?: StatusType;
  onClick: () => void;
  buttonLabel: string;
}

interface ILiveChatStatusProps {
  status: StatusType;
}

const ACTIVE_GREEN = "#a5bb48";
const StatusColor = {
  UNAVAILABLE: Colors.GREY48,
  AVAILABLE: ACTIVE_GREEN,
  BUSY: Colors.RED,
  WAITING: Colors.GREY48,
  LOADING: Colors.GREY48,
  ERROR: Colors.GREY48,
};

const Pulse = {
  UNAVAILABLE: false,
  AVAILABLE: true,
  BUSY: false,
  WAITING: true,
  LOADING: true,
  ERROR: false,
};

export const LiveChatStatus = ({
  status,
}: ILiveChatStatusProps): JSX.Element => {
  const fill = StatusColor[status];
  const label = content.liveChat[status.toLowerCase()]?.label;
  const pulsing = Pulse[status];

  return (
    <Columns space="xxsmall">
      <Column width="content">
        <svg
          data-id="status-bubble"
          viewBox="14px -14px 14px 14px"
          height="20px"
          width="20px"
        >
          <circle cx="50%" cy="55%" r="5" fill={fill}>
            {pulsing && (
              <>
                <animate
                  attributeName="r"
                  from="5"
                  to="12"
                  dur="1.5s"
                  begin="0s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  from="1"
                  to="0"
                  dur="1.5s"
                  begin="0s"
                  repeatCount="indefinite"
                />
              </>
            )}
          </circle>
          <circle cx="50%" cy="55%" r="5" fill={fill} />
        </svg>
      </Column>
      <Column width="flex">
        <Text data-id="status-label">{label}</Text>
      </Column>
    </Columns>
  );
};

export const LiveChatItem = ({
  title,
  icon,
  line1,
  line2,
  status = StatusType.UNAVAILABLE,
  onClick,
  buttonLabel,
}: ILiveChatItem): JSX.Element => {
  const showButton =
    status === StatusType.AVAILABLE || status === StatusType.LOADING;
  const loading = status === StatusType.LOADING;
  return (
    <Stack data-id="live-chat" space="large" alignX="left">
      <Columns space="small">
        <Column width="content">{icon}</Column>
        <Column width="flex">
          <Heading variant="h4">{title}</Heading>
        </Column>
      </Columns>
      <Stack space="small">
        <LiveChatStatus status={status} />
        <Text markdown={true}>{line1}</Text>
        <Text markdown={true}>{line2}</Text>
      </Stack>
      {showButton && (
        <Button
          data-id="live-chat-button"
          text={buttonLabel}
          onClick={onClick}
          disabled={loading}
          spinner={loading}
        />
      )}
    </Stack>
  );
};
